import React from 'react';
import ReactDom from 'react-dom';
import styled from 'styled-components';
import { warmGrey, fontWeight, breakpoints } from '@mergermarket/acuris-react-styles';
import { CrossIcon } from '@mergermarket/acuris-react-icon';
import FocusContext from '@mergermarket/acuris-react-focus-context';

const backgroundColor = warmGrey.III;
const arrowWidth = 8;
function positionArrowToNode(arrowPosX = -1, isAnimated = false) {
  return `
  &:before,
  &:after{
    display: ${arrowPosX > -1 ? 'block' : 'none'}
    left: ${arrowPosX}px;
    
    ${isAnimated ? 'transition: left 300ms linear;' : ''}
  }
  `;
}

function getArrowStyle() {
  return `
  &:before,
  &:after {
    content: "";
    border-left: ${arrowWidth}px solid transparent;
    border-right: ${arrowWidth}px solid transparent;
    position: absolute;
    width: 12px;
  }

  &:before {
    border-bottom: ${arrowWidth}px solid ${warmGrey.V};
    top: -27px;
    height: 26px;
  }

  &:after {
    border-bottom: ${arrowWidth}px solid ${warmGrey.III};
    top: -27px;
    height: 28px;
  }
`;
}

const ArrowBoxContainer = styled.div`
  background: ${backgroundColor};
  margin: 0;
  padding: 0;
  border-top: ${props => props.isOpen ? `1px` : 0} solid ${warmGrey.V};
  transition-timing-function: ${props => props.isOpen ? `ease-in` : `ease-out`};
  transition: ${props => props.isOpen ? `0` : `0.35s`};
`;
const Arrow = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  ${props => `
    ${getArrowStyle()}
    ${positionArrowToNode(props.arrowPosX, props.isAnimated)}
  `};
`;
const ContentContainer = styled.section`
  max-width: 1600px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  height: ${props => props.contentContainerHeight && props.content !== null ? `${props.contentContainerHeight}px` : 0};
  opacity: ${props => props.content !== null ? 1 : 0};
  transition: height 0.35s cubic-bezier(0.52, 1.14, 1, 1), opacity 0.25s cubic-bezier(0.52, 1.14, 1, 1);
`;
const Content = styled.div`
  margin: 0;
  padding: 0;
  height: auto;
`;
const MaskForBottomCloseButton = styled.div`
  height: calc(100% - 38px);
  width: 100px;
  position: absolute;
  top: 38px;
  right: 0;
  overflow: hidden;
  pointer-events: none;

  .masked-button {
    pointer-events: all;
  }
`;

const TextButton = styled.button`
  font-size: 1.2rem;
  background: transparent;
  border: 0;
  color: ${warmGrey.IX};
  padding: 1.2rem 1rem 1.2rem;
  line-height: 0;
  font-weight: ${fontWeight.bold};

  &:hover {
    text-decoration: underline;
  }
`;
const CloseIcon = styled(CrossIcon)`
  margin-top: -0.1rem;
  margin-left: 0.2rem;
  fill: ${warmGrey.VII};
  transform: rotate(45deg);
`;

const CloseButton = ({
  className,
  onClick,
  tabIndex
}) => /*#__PURE__*/React.createElement(TextButton, {
  tabIndex: tabIndex,
  "data-test-arrowbox-close-icon": "true",
  onClick: onClick,
  className: className
}, "Close ", /*#__PURE__*/React.createElement(CloseIcon, {
  className: "icon--small"
}));

const ArrowBoxCloseButton = styled(CloseButton)`
  position: absolute;
  right: 0;
  color: ${warmGrey.VII};
  @media screen and (min-width: ${breakpoints.medium}) {
    right: 0.5rem;
  }
`;
const CloseButtonBottom = styled(ArrowBoxCloseButton)`
  bottom: 1.2rem;
`;
const CloseButtonTop = styled(ArrowBoxCloseButton).attrs({
  tabIndex: -1
})`
  display: none;
  @media screen and (min-width: ${breakpoints.medium}) {
    display: block;
    top: 1rem;
  }
`;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

class ArrowBox extends React.Component {
  /*
  for timeoutTimer would prefer type number but there's a problem due to Node so used 'any' as a workaround.
  See forum https://stackoverflow.com/questions/45802988/typescript-use-correct-version-of-settimeout-node-vs-window
  Other solutions mentioned are to add "types": ["node"] to compilerOptions options in tsConfig
  and then could use type number | null | NodeJS.Timer
  */
  constructor(props) {
    super(props);
    this.timeoutTimer = void 0;
    this.contentRef = void 0;
    this.containerRef = void 0;

    this.handleResize = () => {
      if (this.isFirstRunOrPanelIsOpen()) {
        this.setState({
          shouldAnimateArrow: false
        });
        this.updateArrowPosition();
      }

      this.updateContentHeight();
    };

    this.handleClick = e => {
      const headerInput = document.querySelector('[id*=header-input]');
      const userMenu = document.querySelector('[data-test-usermenu-button]');

      if (this.containerRef.current && !this.containerRef.current.contains(e.target) && !(headerInput === null || headerInput === void 0 ? void 0 : headerInput.contains(e.target)) && !(userMenu === null || userMenu === void 0 ? void 0 : userMenu.contains(e.target))) {
        const elm = document.querySelector('[id*=header-input_field]');
        elm && elm.blur();
        this.props.onClose && this.props.onClose(e);
      }
    };

    this.hasChildren = () => React.Children.count(this.props.children) > 0;

    const initialContent = this.props.children;
    this.state = {
      content: initialContent,
      contentHeight: 0,
      arrowPosX: 0,
      shouldAnimateArrow: false
    };
    this.contentRef = React.createRef();
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.handleResize();

    if (window) {
      window.addEventListener('resize', this.handleResize); // @ts-ignore

      window.addEventListener('click', this.handleClick);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      shouldAnimateArrow: this.hasChildren() && React.Children.count(nextProps.children) > 0
    });
  }

  componentDidUpdate(prevProps) {
    this.updateArrowPosition();
    this.updateContentHeight(); // remove arrowbox content and close buttons after arrowBox close transition is complete

    if (this.props.children !== prevProps.children) {
      if (!this.hasChildren()) {
        this.timeoutTimer = setTimeout(() => {
          this.setState({
            content: null
          });
        }, ArrowBox.ANIMATION_TIME);
      } else {
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
        }

        this.setState({
          content: this.props.children
        });
      }
    }

    if (this.hasChildren()) {
      setTimeout(() => {
        this.updateArrowPosition();
      }, ArrowBox.ANIMATION_TIME * 0.5);
    }
  }

  componentWillUnmount() {
    if (window) {
      window.removeEventListener('resize', this.handleResize); // @ts-ignore

      window.removeEventListener('click', this.handleClick);
    }
  }

  render() {
    const {
      onClose,
      children,
      contentAttributes,
      shouldTabButtons = true
    } = this.props;
    const isArrowBoxOpen = this.state.contentHeight > 0 && children !== null;
    const tabIndexProps = {};

    if (shouldTabButtons === false) {
      tabIndexProps['tabIndex'] = -1;
    }

    return /*#__PURE__*/React.createElement(ArrowBoxContainer, {
      ref: this.containerRef,
      isOpen: isArrowBoxOpen
    }, this.hasChildren() && /*#__PURE__*/React.createElement(Arrow, {
      arrowPosX: this.state.arrowPosX,
      isAnimated: this.state.shouldAnimateArrow
    }), /*#__PURE__*/React.createElement(ContentContainer, _extends({
      "data-test-arrow-box-content-container": true,
      contentContainerHeight: this.state.contentHeight,
      content: children
    }, contentAttributes), this.state.content && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CloseButtonTop, {
      onClick: onClose,
      "data-test-close": true
    }), /*#__PURE__*/React.createElement(Content, {
      className: "arrow-box-content",
      ref: this.contentRef
    }, this.state.content), /*#__PURE__*/React.createElement(MaskForBottomCloseButton, null, /*#__PURE__*/React.createElement(CloseButtonBottom, _extends({}, tabIndexProps, {
      className: "masked-button",
      onClick: onClose,
      "data-test-close": true
    }))))));
  } // Made public for testing purposes. JSDom does not work with offsetHeight


  updateArrowPosition() {
    const locatorRect = this.getLocatorRect();
    const containerRect = this.getContainerRect();

    if (this.hasLocatorPositionOrWidthUpdated(containerRect, locatorRect)) {
      this.setState({
        arrowPosX: this.getArrowPosX(containerRect, locatorRect)
      });
    }
  }

  getLocatorRect() {
    return this.props.arrowLocator && this.props.arrowLocator.getBoundingClientRect();
  }

  getContainerRect() {
    let componentEl;

    try {
      componentEl = ReactDom.findDOMNode(this);
    } catch (e) {} // tslint:disable-line


    return Boolean(componentEl) && componentEl.children[0].getBoundingClientRect();
  }

  isFirstRunOrPanelIsOpen() {
    return this.hasChildren() || Boolean(this.state.arrowPosX);
  }

  hasLocatorPositionOrWidthUpdated(containerRect, locatorRect) {
    return !!locatorRect && !!locatorRect.left && this.state.arrowPosX !== this.getArrowPosX(containerRect, locatorRect) || !this.props.arrowLocator && this.state.arrowPosX !== ArrowBox.ARROW_HIDE_POS;
  }

  updateContentHeight() {
    const prevHeight = this.state.contentHeight;
    requestAnimationFrame(() => {
      const targetHeight = this.hasChildren() ? this.getNewHeight() : 0;

      if (targetHeight !== prevHeight) {
        this.setState({
          contentHeight: targetHeight
        });
      }
    });
  }

  getNewHeight() {
    return this.contentRef.current ? this.contentRef.current.offsetHeight : 0;
  }

  getArrowPosX(containerRect, locatorRect) {
    return !!locatorRect && locatorRect.left - ArrowBox.ARROW_WIDTH + locatorRect.width * 0.5 - (!!containerRect && containerRect.left || 0) || ArrowBox.ARROW_HIDE_POS;
  }

}

ArrowBox.ARROW_HIDE_POS = -1;
ArrowBox.ARROW_WIDTH = 8;
ArrowBox.ANIMATION_TIME = 400;

const ContextArrowBox = () => {
  const context = React.useContext(FocusContext);
  return /*#__PURE__*/React.createElement(React.Fragment, null, context.content && /*#__PURE__*/React.createElement(ArrowBox, {
    "data-test-context-arrow-box": true,
    shouldTabButtons: context.hasTabbedContent,
    onClose: () => {
      context.setContent(null);
      context.setElement(null);
      context.setActiveMenu(null);
    },
    arrowLocator: context.element,
    contentAttributes: context.contentAttributes
  }, context.content));
};

export default ArrowBox;
export { CloseButton, ContextArrowBox };
//# sourceMappingURL=index.js.map
